/**
 * The Tally sync queue — what the connector agent still owes Tally, and what Tally refused.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/table.tsx
 *   the table card itself, through `components/table/data-table.tsx`'s `PagedTable`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero (through `components/detail-parts.tsx`'s `PageHero`);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the figure cards.
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import {
  PiCheckCircleDuotone as CheckCircle2,
  PiFunnelDuotone as FunnelIcon,
  PiHeartbeatDuotone,
  PiListNumbersDuotone as WindowIcon,
  PiTimerDuotone as TimerIcon,
  PiTrayDuotone as Inbox,
} from "react-icons/pi";
import { Badge, Button, Text } from "rizzui";
import { ComplianceStatusBadge, EmptyState, MetricCard } from "@steelyard/theme";
import { PageHero } from "../../../components/detail-parts";
import { PagedTable, type StatusOption } from "../../../components/table/data-table";
import { TableSearch } from "../../../components/table/toolbar";
import { hrefWith } from "../../../lib/paging";
import { queueColumns } from "./columns";
import { Filters } from "./filters";
import {
  checkQueuedInvoiceIrn,
  emptyQueueReason,
  getSyncQueue,
  LATENCY_FLOOR_MINUTES,
  type EmptyQueueReason,
  type SyncQueue,
  type SyncQueueItem,
} from "../../../lib/services/tallySync";
import { invoiceDefectBanner } from "../../invoicing/columns";
import { listInvoices, MAX_INVOICE_PAGE_SIZE, type InvoiceRow } from "../../../lib/services/invoicing";

/**
 * Ported from apps/admin/app/(app)/integrations/tally/queue/page.tsx. The route has no paging
 * (`take` only), so the whole fetched window (up to `TAKE` rows) goes to `PagedTable` as one set and
 * is paged in memory — which is also why its search box (`table.setGlobalFilter`) searches every
 * loaded row. `PagedTable`'s `rowClassName`/`subRow` carry the BR-07 defect banner this screen used
 * to render through its own copy of the `bodyRow` override. Three different empty-table reasons,
 * only one of which is good news.
 */
const TAKE = 200;
const STATUSES = ["pending", "sent", "failed"] as const;

/** What each status means for the reader — the badges themselves are `ComplianceStatusBadge`'s. */
const LEGEND = [
  { state: "pending", text: "Waiting for the agent to post it." },
  { state: "done", text: "Posted to Tally. Never retried — that is how a voucher posts twice." },
  { state: "failed", text: "Refused by Tally. Open it to read why, fix the cause, then retry." },
] as const;

export async function queueLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const status = STATUSES.find((known) => known === params.get("status"));
  const entityType = params.get("type")?.trim() || undefined;
  const filtered = Boolean(status || entityType);

  // T019 (FR-013, BR-07): the queue row itself carries no IRN (contracts/backend-gaps.md GAP-1),
  // so a second, unfiltered call cross-checks queued invoices against Billing's own compliance
  // statuses. One bulk call, not one per row: `invoiceNumber` is matched with `.Contains(...)`
  // server-side, so a per-row filtered call would still need this same exact-match check locally.
  // Billing failing (or refusing) loses only that cross-check, not the queue screen.
  const [queue, invoicePage] = await Promise.all([
    getSyncQueue({ status, entityType, take: TAKE }),
    listInvoices({ page: 1, pageSize: MAX_INVOICE_PAGE_SIZE }).catch(() => null),
  ]);
  const invoicesByNumber = new Map((invoicePage?.items ?? []).map((invoice) => [invoice.invoiceNumber, invoice]));

  return { queue, status, entityType, filtered, invoicesByNumber, crossCheckFailed: invoicePage === null };
}

export default function SyncQueuePage() {
  const { queue, status, entityType, filtered, invoicesByNumber, crossCheckFailed } = useLoaderData() as {
    crossCheckFailed: boolean;
    queue: SyncQueue;
    status?: string;
    entityType?: string;
    filtered: boolean;
    invoicesByNumber: Map<string, InvoiceRow>;
  };
  const [searchParams] = useSearchParams();
  const empty = emptyQueueReason(queue, filtered, Boolean(status));

  const query = (over: Record<string, string | undefined>) => hrefWith("/integrations/tally/queue", searchParams, over);
  // Status is one server-side param; the queue answers no per-status counts, so the options carry none.
  const statusOptions: StatusOption[] = [
    { label: "All records", href: query({ status: undefined, page: undefined }), active: !status },
    ...STATUSES.map((s) => ({
      label: s.charAt(0).toUpperCase() + s.slice(1),
      href: query({ status: s, page: undefined }),
      active: s === status,
      status: s,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Tally Agent", href: "/integrations/tally" }, { name: "Sync Queue" }]}
        icon={<Inbox aria-hidden className="size-9" />}
        iconClassName="bg-secondary"
        title="Tally Sync Queue"
        description={
          <>
            Every record waiting for, sent by, or refused by the connector agent. Showing up to {TAKE} records; invoice IRN
            defects are checked against the {MAX_INVOICE_PAGE_SIZE} most recently issued invoices — an older invoice&rsquo;s
            reference shows no defect banner even if one exists.
            {queue.items.length === TAKE ? " There may be more beyond what's shown here." : ""}
            {crossCheckFailed ? " Billing could not be read, so IRN defects are not checked on this page." : ""}
          </>
        }
        meta={
          filtered ? (
            <>
              <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5">
                <FunnelIcon aria-hidden className="size-4" />
                Filtered
              </Badge>
              {status ? (
                <Badge variant="outline" rounded="pill" className="border-muted text-gray-700">
                  Status: {status}
                </Badge>
              ) : null}
              {entityType ? (
                <Badge variant="outline" rounded="pill" className="border-muted font-mono text-gray-700">
                  Type: {entityType}
                </Badge>
              ) : null}
            </>
          ) : null
        }
        actions={
          <Link to="/integrations/tally">
            <Button as="span" variant="outline">
              <PiHeartbeatDuotone aria-hidden className="me-1.5 size-4 text-green" />
              Agent health
            </Button>
          </Link>
        }
      >
        <ul className="grid gap-4 md:grid-cols-3">
          {LEGEND.map((entry) => (
            <li key={entry.state} className="flex flex-col gap-1.5">
              <ComplianceStatusBadge subject="tally" state={entry.state} />
              <Text className="text-sm text-gray-500">{entry.text}</Text>
            </li>
          ))}
        </ul>
      </PageHero>

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Unsynced records"
          metric={queue.unsyncedCount === null ? <span className="text-gray-500">Not reported</span> : queue.unsyncedCount}
          icon={<Inbox aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">The service&rsquo;s own count of records not yet in Tally.</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Records in this view"
          metric={queue.items.length}
          icon={<WindowIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {queue.items.length === TAKE ? `The first ${TAKE} — there may be more.` : `Up to ${TAKE} are loaded at a time.`}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Latency floor"
          metric={`${LATENCY_FLOOR_MINUTES} minutes`}
          icon={<TimerIcon aria-hidden className="size-6" />}
          iconClassName="bg-red text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">A record queued longer than this is flagged in red below.</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      <PagedTable<SyncQueueItem>
        result={{ items: queue.items, page: 1, pageSize: queue.items.length, totalCount: queue.items.length }}
        columns={queueColumns()}
        getRowId={(row) => row.itemId}
        status={{ label: "Status", options: statusOptions }}
        caption="Tally sync queue: reference, type, status, Tally's response, attempts and last attempt"
        // The route has no search parameter: this searches the window already fetched (up to TAKE rows).
        search={(table) => <TableSearch table={table} placeholder="Search references and errors…" />}
        filters={(table) => <Filters table={table} entityType={entityType} query={query} />}
        clear={{ active: filtered, href: query({ status: undefined, type: undefined, page: undefined }) }}
        // BR-07: a queued invoice with no IRN is marked, and its banner shown unconditionally.
        rowClassName={(row) => (defectInvoice(row, invoicesByNumber) ? "border-red/40 bg-red/5 border-l-4" : undefined)}
        subRow={(row) => {
          const invoice = defectInvoice(row, invoicesByNumber);
          return invoice ? invoiceDefectBanner(invoice) : null;
        }}
        empty={empty ? <EmptyStateForReason reason={empty} clearHref={query({ status: undefined, type: undefined })} /> : null}
      />
      {/* A queued invoice with no IRN renders as a defect banner above (T019) instead of being
          named here as unshowable. A record past §19's 15-minute floor is flagged in the "Queued
          (UTC)" column (T018, FR-012, GAP-1 closed 2026-09-14) instead of being named here too. */}
    </div>
  );
}

/** The queued invoice this row is a BR-07 defect for, or null. `checkQueuedInvoiceIrn` decides; nothing here does. */
function defectInvoice(row: SyncQueueItem, invoicesByNumber: Map<string, InvoiceRow>): InvoiceRow | null {
  const check = checkQueuedInvoiceIrn(row, invoicesByNumber);
  return check?.status === "defect" ? check.invoice : null;
}

function EmptyStateForReason({ reason, clearHref }: { reason: EmptyQueueReason; clearHref: string }) {
  if (reason === "drained") {
    return (
      <EmptyState title="The queue is drained" icon={<CheckCircle2 aria-hidden className="size-16 text-green" />}>
        Every record here has been posted to Tally. Nothing is waiting and nothing has failed — this is the integration
        working, not an absence of data.
      </EmptyState>
    );
  }

  if (reason === "filtered") {
    return (
      <EmptyState
        title="No records matched this filter"
        action={
          <Link to={clearHref}>
            <Button as="span" size="sm" variant="outline">
              Clear the filter and show the whole queue
            </Button>
          </Link>
        }
      >
        Nothing matched, which says nothing about the rest of the queue — the filter removed the evidence either way. Clear it
        to tell &ldquo;nothing raised&rdquo; from &ldquo;drained&rdquo;.
      </EmptyState>
    );
  }

  return (
    <EmptyState title="Nothing has been raised to Tally" icon={<Inbox aria-hidden className="size-16 text-secondary" />}>
      No invoice, credit note or customer has entered this queue at all. That is different from a queue the agent has emptied:
      there has been nothing for it to do.
    </EmptyState>
  );
}
